import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { Application } from '../lib/api';
import {
  Empty,
  ErrorNotice,
  Loading,
  Notice,
  Panel,
  formatBytes,
  relativeTime,
  useAsync,
} from '../components/ui';
import { MetricCard } from '../components/chrome';
import { IconApps, IconCheck, IconClock, IconCloud, IconUpload } from '../components/icons';

interface UploadResult {
  applicationId: string;
  apkVersionId: string;
  applicationCreated: boolean;
  signaturePinned: boolean;
  warnings: string[];
  message: string;
  metadata: {
    packageName: string;
    versionCode: number;
    versionName: string;
    label: string | null;
    minSdkVersion: number | null;
    targetSdkVersion: number | null;
    permissions: string[];
    signatureSchemes: string[];
    sha256: string;
    fileSizeBytes: number;
    signer: {
      certificateSha256: string | null;
      subject: string | null;
      validFrom: string | null;
      validTo: string | null;
      parseNote: string | null;
    };
  };
}

export default function ApplicationsPage() {
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [query, setQuery] = useState('');
  const [drag, setDrag] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const list = useAsync((signal) => api.get<{ applications: Application[] }>('/applications', signal), []);
  const apps = list.data?.applications ?? [];
  const deployed = apps.filter((a) => a.installed_device_count > 0).length;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter((a) =>
      a.display_name.toLowerCase().includes(q) || a.package_name.toLowerCase().includes(q),
    );
  }, [apps, query]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const slice = filtered.slice((page - 1) * pageSize, page * pageSize);

  const takeFile = (next: File | null) => {
    if (!next) return;
    if (!next.name.toLowerCase().endsWith('.apk')) {
      setError(new Error('Choose an .apk file.'));
      return;
    }
    setFile(next);
    setError(null);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append('apk', file);
      if (notes) form.append('notes', notes);
      const uploaded = await api.upload<UploadResult>('/applications/upload', form);
      setResult(uploaded);
      setFile(null);
      setNotes('');
      if (fileInput.current) fileInput.current.value = '';
      list.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Applications</h2>
          <p>Any Android application can be managed here — the system is not tied to one APK.</p>
        </div>
        <div className="apps-hero-art">
          <div>
            <strong>Manage. Secure. Deploy.</strong>
            <div className="small muted">Upload a signed APK. The server reads the rest.</div>
          </div>
        </div>
      </div>

      <ErrorNotice error={error} />

      <div className="metric-row three">
        <MetricCard tone="blue" icon={<IconApps />} label="Total Applications" value={apps.length} />
        <MetricCard tone="green" icon={<IconCheck />} label="Deployed to Devices" value={deployed} />
        <MetricCard tone="purple" icon={<IconClock />} label="Pending Deployment" value={Math.max(0, apps.length - deployed)} />
      </div>

      <Panel title="Upload new application">
        <div className="upload-split">
          <div
            className={`dropzone ${drag ? 'over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              takeFile(e.dataTransfer.files?.[0] ?? null);
            }}
          >
            <IconCloud />
            <p>
              Drag and drop your APK file here or click to browse from your computer.
            </p>
            <button type="button" onClick={() => fileInput.current?.click()}>Choose File</button>
            <p className="small muted">{file ? file.name : 'Supported format .apk · max size depends on server limits'}</p>
            <input
              ref={fileInput}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              hidden
              onChange={(e) => takeFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div>
            <label>
              <span>Notes (optional)</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. adds offline exam caching" />
            </label>
            <button className="primary" onClick={upload} disabled={!file || busy}>
              <IconUpload /> {busy ? 'Uploading and validating…' : 'Upload Application'}
            </button>
            <p className="small muted">
              The server reads the package name, version, SDK levels and signing certificate from the file itself.
            </p>
          </div>
        </div>
      </Panel>

      {result && (
        <Panel title="Upload result">
          <Notice kind="success">{result.message}</Notice>
          {result.warnings.map((w, i) => (
            <Notice key={i} kind="warning">{w}</Notice>
          ))}
          {result.applicationCreated && (
            <Notice kind="info">
              A new application record was created for <code>{result.metadata.packageName}</code>.
              {result.signaturePinned && ' Its signing certificate is now pinned — future uploads must use the same key.'}
            </Notice>
          )}
          <dl className="kv">
            <dt>Package</dt><dd className="mono">{result.metadata.packageName}</dd>
            <dt>Label</dt><dd>{result.metadata.label ?? '—'}</dd>
            <dt>Version</dt><dd>{result.metadata.versionName} (code {result.metadata.versionCode})</dd>
            <dt>Size</dt><dd>{formatBytes(result.metadata.fileSizeBytes)}</dd>
          </dl>
          <div className="actions" style={{ marginTop: 12 }}>
            <Link className="button" to={`/applications/${result.applicationId}`}>View</Link>
            <Link className="button primary" to={`/deployments?apk=${result.apkVersionId}`}>Deploy this version</Link>
          </div>
        </Panel>
      )}

      <Panel
        title="Managed applications"
        actions={
          <input
            type="search"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search applications…"
            aria-label="Search applications"
            style={{ maxWidth: 240 }}
          />
        }
      >
        {list.loading && !list.data ? (
          <Loading what="applications" />
        ) : slice.length === 0 ? (
          <Empty>No applications uploaded yet.</Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Application</th><th>Package</th><th>Latest version</th>
                    <th className="num">Versions</th><th className="num">On devices</th>
                    <th>Uploaded</th><th>Signature</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {slice.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <Link to={`/applications/${a.id}`}>{a.display_name}</Link>
                      </td>
                      <td className="mono small">{a.package_name}</td>
                      <td>
                        {a.latest_version_name ? `v${a.latest_version_name}` : '—'}
                        <div className="small muted">code {a.latest_version_code ?? '—'}</div>
                      </td>
                      <td className="num">{a.version_count}</td>
                      <td className="num">{a.installed_device_count}</td>
                      <td className="small muted">{relativeTime(a.latest_uploaded_at)}</td>
                      <td>
                        {a.signing_cert_sha256
                          ? <span className="badge ok">PINNED</span>
                          : <span className="badge muted">UNPINNED</span>}
                      </td>
                      <td>
                        <Link className="button small" to={`/applications/${a.id}`}>View</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-foot">
              <span className="small muted">{pageSize} per page</span>
              <div className="pager">
                <button type="button" className="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
                <span className="page-num">{page}</span>
                <button type="button" className="small" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>›</button>
              </div>
            </div>
          </>
        )}
      </Panel>
    </>
  );
}
